/**
 * AutoCaptions — Project history (logged-in users only).
 *
 * Lists the user's past AutoCaptions projects from MongoDB and lets them
 * jump back into any one. Designed as a lightweight dashboard, not a full
 * project manager.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Sparkles, Clock, Play, AlertTriangle, Loader2, History as HistoryIcon } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SignInRequiredCard from "./SignInRequiredCard";
import "./AutoCaptionsPage.css";

const API = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_LABELS = {
  uploaded: "Uploaded",
  transcribing: "Transcribing",
  ready_to_edit: "Ready to style",
  rendering: "Rendering",
  done: "Done",
  failed: "Failed",
};

export default function AutoCaptionsHistoryPage() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState(null);   // null = loading, [] = empty
  const [authed, setAuthed] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/api/auto-captions/my-projects`, {
      withCredentials: true,
    })
      .then((r) => {
        setAuthed(!!r.data?.authenticated);
        setProjects(r.data.projects || []);
      })
      .catch((err) => {
        setError(err?.response?.data?.detail || err.message || "Could not load projects.");
        setProjects([]);
        setAuthed(false);
      });
  }, []);

  return (
    <>
      <Header />
      <div className="ac-page" data-testid="auto-captions-history">
        <div className="ac-inner">
          <button
            type="button"
            className="ac-link"
            onClick={() => navigate("/tools/auto-captions")}
            data-testid="ac-history-back"
            style={{ marginBottom: 12 }}
          >
            <ArrowLeft size={12}/> Back to AutoCaptions
          </button>
          <span className="ac-eyebrow"><Sparkles size={12}/> AutoCaptions · history</span>
          <h1 className="ac-h1">Your captioned <em>videos</em>.</h1>
          <p className="ac-sub">
            Every video you&rsquo;ve captioned with MarkeeMix shows up here. Pick one to
            re-download, restyle, or share.
          </p>

          {authed === false && (
            <SignInRequiredCard
              icon={<HistoryIcon size={26} strokeWidth={1.6}/>}
              eyebrow="AutoCaptions · History"
              title="Sign in to see your saved projects"
              description="Every video you caption with MarkeeMix is saved to your account so you can re-download, restyle, or share it later."
              benefits={[
                "Resume any project anytime",
                "Re-download MP4, SRT, ASS",
                "Re-share the public link",
              ]}
              testId="ach-signin-required"
            />
          )}

          {error && authed !== false && (
            <div className="ac-error" data-testid="ac-history-error">
              <AlertTriangle size={16}/> {error}
            </div>
          )}

          {authed === null && projects === null && (
            <div className="ac-card" style={{ textAlign: "center" }}>
              <Loader2 size={22} className="ac-spin" style={{ color: "var(--ac-purple)" }}/>
              <p style={{ marginTop: 12, color: "var(--ac-text-soft)" }}>Loading your projects…</p>
            </div>
          )}

          {authed === true && projects && projects.length === 0 && !error && (
            <div className="ac-card" style={{ textAlign: "center", padding: 60 }} data-testid="ac-history-empty">
              <Play size={36} style={{ color: "var(--ac-purple)", marginBottom: 16 }}/>
              <h3 style={{ margin: "0 0 8px" }}>No projects yet</h3>
              <p style={{ color: "var(--ac-text-soft)", marginBottom: 24 }}>
                Caption your first video to see it here.
              </p>
              <button className="ac-btn" onClick={() => navigate("/tools/auto-captions")} data-testid="ac-history-start">
                Start a new project <ArrowRight size={14}/>
              </button>
            </div>
          )}

          {authed === true && projects && projects.length > 0 && (
            <div className="ac-history-grid" data-testid="ac-history-grid">
              {projects.map((p, i) => (
                <motion.button
                  key={p.project_id}
                  type="button"
                  className="ac-history-card"
                  onClick={() => navigate(`/tools/auto-captions/projects/${p.project_id}`)}
                  data-testid={`ac-history-item-${i}`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: Math.min(i, 6) * 0.04 }}
                >
                  <div className="ac-history-thumb">
                    <Play size={20}/>
                  </div>
                  <div className="ac-history-info">
                    <p className="ac-history-title">{p.title || "Untitled"}</p>
                    <p className="ac-history-meta">
                      <span className={`ac-pill ac-pill--${p.status}`}>{STATUS_LABELS[p.status] || p.status}</span>
                      <span><Clock size={11}/> {formatDuration(p.duration_sec)}</span>
                      <span>{(p.language || "auto").toUpperCase()}</span>
                    </p>
                    <p className="ac-history-when">{formatWhen(p.created_at)}</p>
                  </div>
                  <ArrowRight size={16} className="ac-history-arrow"/>
                </motion.button>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
}

function formatDuration(sec) {
  if (!sec) return "—";
  const s = Math.round(sec);
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, "0")}`;
}
function formatWhen(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch { return iso; }
}
